import { useNavigate } from "react-router-dom";

import { DateTime } from "@bps/date-time";
import { Link, MessageBar, MessageBarType } from "@bps/fluent-ui";
import { FieldDevice } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useFieldDevices } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { getPagesResult } from "@libs/paging/paging.utils";

interface OverviewBannersProps {
  device: FieldDevice;
}

export const OverviewBanners = ({ device }: OverviewBannersProps) => {
  const navigate = useNavigate();
  const { data: deviceData } = useFieldDevices({
    searchTerm: device.cloudConnectorId
  });

  const now = DateTime.now();

  const hasExpired =
    device.tokenExpiryUtc != null && now >= device.tokenExpiryUtc;

  const devices = getPagesResult(deviceData);

  const multipleDevicesSameCCID =
    devices.filter(d => d.cloudConnectorId === device.cloudConnectorId).length >
    1;

  const currentDeviceDetails = devices.find(d => d.id === device.id);
  const multipleDevicesSameCCIDAndMachineGuid =
    multipleDevicesSameCCID &&
    devices.filter(d => d.machineGuid === currentDeviceDetails?.machineGuid)
      .length > 1;

  return (
    <>
      {device.tokenExpiryUtc === undefined && (
        <MessageBar messageBarType={MessageBarType.error}>
          No token has been issued for this device. Please ensure the device is
          active to initiate a request for an authentication token.
        </MessageBar>
      )}
      {multipleDevicesSameCCID && !multipleDevicesSameCCIDAndMachineGuid && (
        <MessageBar messageBarType={MessageBarType.warning}>
          This device shares the same <b>CloudConnectorId</b> as another device,
          which may indicate that it is a duplicate.
        </MessageBar>
      )}

      {multipleDevicesSameCCIDAndMachineGuid && (
        <MessageBar messageBarType={MessageBarType.severeWarning}>
          This device shares the same <b>CloudConnectorId</b> and{" "}
          <b>MachineGuid</b> as another device, which indicates that it is a
          duplicate.
        </MessageBar>
      )}
      {hasExpired && (
        <MessageBar messageBarType={MessageBarType.warning}>
          The latest token has expired. Please visit the{" "}
          <Link
            onClick={() => navigate(`/field-mgmt/${device.id}/fd-security`)}
          >
            device security
          </Link>{" "}
          page, complete any necessary authentication actions and review the
          device secret used for this device.
        </MessageBar>
      )}
    </>
  );
};
